/**
 * THE ELEMENT REFUSALS, and the person's answer to them (element ids step 3,
 * KPR-238 P4; keepr-web docs/plans/element-ids-writers-and-closing.md §4,
 * the owner's ruling: "Queries should NEVER use an ambiguous name. We must
 * always use IDs").
 *
 * Since P3 the API writes every element a filter names as the element's id,
 * `#k7f3q2xa`. A NAME it cannot settle is refused (keepr-api docs/SCHEMA.md
 * "Element names, written as ids"; its openapi `ElementRefusal`):
 *
 *   400 ambiguous_element   several elements answer to the name where it
 *                           reaches: `{ field, element, path, choiceKey,
 *                           reference?, candidates: [{ elementId, label,
 *                           name, cards: [{ cardId, name, qualifier? }] }] }`
 *                           — `qualifier` (KPR-275) where another listed card
 *                           has the same name: `global`, the collection's
 *                           name, "another collection", or the card's key
 *   400 unknown_element     none does (a saved text; a typed `?q=` keeps its
 *                           no-match instead): `{ field, element, path,
 *                           reference? }` — its message names the card when
 *                           the text is pinned to one the writer reads
 *
 * A chart's and a dashboard's save carry the same keys under `invalid_filter`
 * with the code as `cause`; a dashboard run's slot error does too (`error`).
 *
 * THE ANSWER is the same request again with `elementChoices`: a map from the
 * refusal's `choiceKey` to one element id, or to a list of them — "Any of
 * these cards", an ELEMENT GROUP the server writes as `(#a = x or #b = x)`.
 * A body field on a write, a query parameter (JSON) on a typed query —
 * `GET /api/items`, `/count`, `/counts` and the exports. A name the text
 * holds twice needs one pick; a second ambiguous name is a second refusal,
 * and its pick joins the first (`withElementChoice`).
 */

import { parse, serialize, isEntryCountPath } from './kql';

export const ELEMENT_REFUSAL_CODES = Object.freeze(['ambiguous_element', 'unknown_element']);
/** A text too long once its picks are written (a group repeats its condition: 2,000 characters, 50 conditions). */
export const TOO_LONG_CODE = 'query_too_long';
/** The request field (body) or parameter (query) the pick travels in. */
export const ELEMENT_CHOICES_FIELD = 'elementChoices';
/** The option that picks every candidate: the element group. */
export const ANY_OF_THESE_CARDS = 'Any of these cards';

const ELEMENT_ID = /^#?[a-z0-9]{8}$/;
const str = value => (value === undefined || value === null ? '' : String(value));

const isElementCode = code => ELEMENT_REFUSAL_CODES.includes(code);

/** One candidate as the server lists it, or null when it names no element id. */
function candidateOf(raw) {
	const id = str(raw?.elementId).replace(/^#/, '');
	if (!ELEMENT_ID.test(id)) return null;
	const cards = (Array.isArray(raw?.cards) ? raw.cards : [])
		.filter(card => card && typeof card === 'object')
		// A holder's own name for the element, where it differs (a renamed
		// copy on a card beneath: the server's confirmation in access text),
		// and what tells it apart from another listed card of its name
		// (KPR-275: "global", the collection it belongs to, or its key).
		.map(card => ({
			cardId: str(card.cardId),
			name: str(card.name),
			...(card.element ? { element: str(card.element) } : {}),
			...(str(card.qualifier).trim() ? { qualifier: str(card.qualifier).trim() } : {}),
		}));
	// A renamed copy on a card the writer cannot open (P3, L2): never named,
	// but picking the element reads it too.
	return { elementId: id, label: str(raw?.label) || str(raw?.name), name: str(raw?.name), cards, ...(raw?.hiddenCopies === true ? { hiddenCopies: true } : {}) };
}

/**
 * The element refusal a 400's body holds — its own code, or the `cause` of a
 * chart's or a dashboard's `invalid_filter` (and of a dashboard run's slot
 * error) — normalized; null for any other body.
 */
export function elementRefusalOf(body) {
	if (!body || typeof body !== 'object') return null;
	const code = isElementCode(body.code) ? body.code : (isElementCode(body.cause) ? body.cause : null);
	if (!code) return null;
	const element = str(body.element);
	return {
		code,
		message: str(body.message),
		field: str(body.field),
		element,
		path: str(body.path) || element,
		// The place the pick answers. Every refusal of P3's names it; one
		// that does not is answered at the name as written.
		choiceKey: str(body.choiceKey) || element,
		reference: body.reference === true,
		// In text that decides who sees what, where a missing value matches
		// at this place (P3's security review, I2 and its re-check): only
		// every candidate — "Any of these cards". A card the text pins
		// exempts nothing.
		requiresAll: code === 'ambiguous_element' && body.requiresAll === true,
		// …and where a candidate sits on a card the writer cannot open, not
		// even that (P3, `unpickable`): nothing to pick — only someone who can
		// open every card can write the rule. The server's sentence says so.
		unpickable: code === 'ambiguous_element' && body.unpickable === true,
		candidates: code === 'ambiguous_element'
			? (Array.isArray(body.candidates) ? body.candidates : []).map(candidateOf).filter(Boolean)
			: [],
	};
}

/** An axios error's element refusal (a 400 only), or null. */
export function elementRefusalOfError(error) {
	if (error?.response?.status !== 400) return null;
	return elementRefusalOf(error.response.data);
}

/** Can the person settle it by picking? An ambiguous name with a candidate to pick — never an `unpickable` one. */
export const isPickableElementRefusal = refusal => refusal?.code === 'ambiguous_element' && !refusal.unpickable && refusal.candidates.length > 0;

/** An ambiguous name nobody here may answer (`unpickable`): said in the server's words, the text kept. */
export const isUnpickableElementRefusal = refusal => refusal?.code === 'ambiguous_element' && refusal.unpickable === true;

/**
 * A holder card in words: "Suppressor", or "Suppressor (global)" where the
 * server told it apart from another listed card of the same name (KPR-275).
 */
export const holderName = card => (card?.name && card.qualifier ? `${card.name} (${card.qualifier})` : str(card?.name));

/**
 * Two card names that read the same on a button: one Unicode form, runs of
 * spaces as one (HTML collapses them), trimmed, any case — keepr-api
 * elementPass's rule for a qualifier (KPR-275's review, C2).
 */
export const sameCardName = name => str(name).normalize('NFC').replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * KPR-275, for a page that MERGES several refusals into one question (a
 * dashboard run asks once per chart card): the server told each refusal's
 * cards apart on its own, so two cards from two refusals can still read
 * alike ("Caliber on Suppressor" twice, the review's R275-D1). The server's
 * rule again, over the merged candidates, with what the page knows: every
 * card is spelled as shown (`holderName`), qualified or not, until no two read
 * alike — a clashing card with no qualifier takes `ownOf(cardId)` (`global`,
 * its collection's name where the page has it, else its key); where none in a
 * group can, the ones not at their key take `keyOf(cardId)`. A card moves at
 * most twice. NEW candidates; ids, labels and order untouched; a card that
 * reads like no other is left as the server sent it.
 */
export function tellCardsApart(candidates, { ownOf = () => '', keyOf = () => '' } = {}) {
	const list = Array.isArray(candidates) ? candidates : [];
	const nameOf = new Map();
	const out = new Map();
	list.forEach(candidate => (candidate?.cards || []).forEach((card) => {
		if (!card?.cardId || nameOf.has(card.cardId)) return;
		nameOf.set(card.cardId, card.name);
		if (card.qualifier) out.set(card.cardId, card.qualifier);
	}));
	const ids = [...nameOf.keys()];
	const key = id => str(keyOf(id)).trim();
	for (let round = 0; round <= 2 * ids.length; round += 1) {
		const byName = new Map();
		ids.forEach((id) => {
			const shown = sameCardName(holderName({ name: nameOf.get(id), qualifier: out.get(id) }));
			if (!byName.has(shown)) byName.set(shown, []);
			byName.get(shown).push(id);
		});
		const groups = [...byName.values()].filter(group => group.length > 1);
		if (!groups.length) break;
		let changed = false;
		groups.forEach((group) => {
			let next = group.filter(id => !out.has(id)).map(id => [id, str(ownOf(id)).trim()]).filter(([, q]) => q);
			if (!next.length) next = group.filter(id => key(id) && out.get(id) !== key(id)).map(id => [id, key(id)]);
			next.forEach(([id, q]) => { out.set(id, q); changed = true; });
		});
		if (!changed) break;
	}
	return list.map(candidate => ({
		...candidate,
		cards: (candidate?.cards || []).map(card => (out.has(card.cardId) && out.get(card.cardId) !== card.qualifier ? { ...card, qualifier: out.get(card.cardId) } : card)),
	}));
}

/** "Status on Task" — a candidate in words: its label, and the cards that hold it. */
export function candidateLabel(candidate) {
	const holders = candidate?.cards || [];
	// A holder under the element's own name, and one under another — a
	// renamed copy on a card beneath, which the element reads too (P3's
	// access refusals name each holder's `element`; a card may be both).
	const own = [...new Set(holders.filter(card => card.name && (!card.element || card.element === candidate?.name)).map(holderName))];
	const copies = holders.filter(card => card.element && card.element !== candidate?.name);
	const label = candidate?.label || candidate?.name || 'An element';
	const base = own.length ? `${label} on ${own.join(', ')}` : label;
	const listed = copies.map(card => (card.name ? `${asWords(card.element)} on ${holderName(card)}` : asWords(card.element)));
	const withCopies = copies.length ? `${base} and its ${copies.length > 1 ? 'copies' : 'copy'} ${listed.join(', ')}` : base;
	return candidate?.hiddenCopies ? `${withCopies}, ${HIDDEN_COPY_NOTE}` : withCopies;
}

/** What a candidate also reads, where a copy of it sits on a card the person cannot open (never named). */
export const HIDDEN_COPY_NOTE = 'also read through a copy on a card you can’t open';

/**
 * A CONFIRMATION, not a question (P3's security re-check, access text only):
 * `requiresAll` with ONE candidate — one element held under several names (a
 * renamed copy on a card beneath). Its `cards` name each holder with its own
 * element name (`element`). Confirming sends that one id.
 */
export const isElementConfirmation = refusal => isPickableElementRefusal(refusal)
	&& refusal.requiresAll && refusal.candidates.length === 1;

/** A name as words: "state_copy" → "State_copy". */
const asWords = name => (name ? name.charAt(0).toUpperCase() + name.slice(1) : name);

/**
 * "This rule will read Status on Bug and its copy State_copy on
 * Copychild." — what a confirmation reads: the element on each card that
 * holds it under its own name, a holder under another name as its copy.
 */
export function confirmationSentence(refusal) {
	const [candidate] = refusal?.candidates || [];
	if (!candidate) return '';
	// A copy on a card the person cannot open is never listed: the server's
	// own sentence says that much.
	if (!candidate.hiddenCopies && !candidate.cards.some(card => card.element && card.element !== candidate.name)) return refusalSentence(refusal);
	return `This rule will read ${candidateLabel(candidate)}.`;
}

/**
 * What "Any of these cards" reads, where it is all a window may offer (who
 * sees what): every candidate by its label and cards, a copy's own name
 * included — so the pick is made knowing what it admits.
 */
export const groupReadsSentence = refusal => (isPickableElementRefusal(refusal)
	? `${ANY_OF_THESE_CARDS} reads ${refusal.candidates.map(candidateLabel).join('; ')}.`
	: '');

/** The one button a confirmation offers. */
export const CONFIRM_ELEMENT = 'Confirm';

/**
 * What the person can pick: each candidate (its id), then — where there are
 * two or more — "Any of these cards" (every id: the element group).
 * `[{ key, value, label }]`; `value` is what `withElementChoice` takes; keys
 * are the element id and its cards, so one element held under several names
 * renders as one option.
 *
 * Who sees what (`requiresAll`, or `scope.groupOnly` — the text read as the
 * server reads it, `accessPickScope`): "Any of these cards" alone; a single
 * candidate there is a confirmation (`isElementConfirmation`): "Confirm",
 * which sends that one id.
 */
export function elementPickOptions(refusal, scope = null) {
	if (!isPickableElementRefusal(refusal)) return [];
	const keyOf = candidate => `${candidate.elementId}:${candidate.cards.map(card => card.cardId).join(',')}`;
	if (isElementConfirmation(refusal)) {
		const [only] = refusal.candidates;
		return [{ key: `${keyOf(only)}:confirm`, value: only.elementId, label: CONFIRM_ELEMENT }];
	}
	const any = refusal.candidates.length > 1
		? [{ key: 'any', value: refusal.candidates.map(candidate => candidate.elementId), label: ANY_OF_THESE_CARDS }]
		: [];
	// The server's own word first (`requiresAll`): every candidate, nothing less.
	if (pickNeedsAll(refusal, scope) && any.length) return any;
	return [
		...refusal.candidates.map(candidate => ({ key: keyOf(candidate), value: candidate.elementId, label: candidateLabel(candidate) })),
		...any,
	];
}

// ------------------------------------------------------- who sees what

const NEGATIVE_CMPS = new Set(['ne', 'nin', 'ncontains', 'empty']);

/**
 * A repeating group's COUNT (keepr-api KPR-681): `ingredient.count = 0`
 * reads a missing group as 0 entries, so a missing value matches exactly
 * when 0 satisfies the comparison — the server's rule (elementPass
 * `countMatchesMissing`). Whether the root IS a group is the refusal's word
 * (its candidates' `dataType`): on an item lookup, `x.count` is a dot-walk
 * into the record's own `count`, and the operator alone decides.
 */
const COUNT_POSITIVE = { ne: 'eq', nin: 'in', ncontains: 'contains', nempty: 'empty' };
function countMatchesMissing(node) {
	const negated = Object.prototype.hasOwnProperty.call(COUNT_POSITIVE, node.cmp);
	const cmp = negated ? COUNT_POSITIVE[node.cmp] : node.cmp;
	const literal = (v) => {
		if (!v || typeof v !== 'object') return NaN;
		if (v.type === 'num') return Number.isFinite(v.v) ? v.v : NaN;
		const text = v.type === 'str' ? String(v.v).trim() : '';
		return /^-?\d+(?:\.\d+)?$/.test(text) ? Number(text) : NaN;
	};
	const values = !node.value ? [] : (node.value.type === 'list' ? node.value.v || [] : [node.value]);
	const numbers = values.map(literal);
	let zero = false;
	if (cmp === 'eq' || cmp === 'in') zero = numbers.some(n => n === 0);
	else if (['gt', 'gte', 'lt', 'lte'].includes(cmp) && Number.isFinite(numbers[0])) {
		zero = { gt: 0 > numbers[0], gte: 0 >= numbers[0], lt: 0 < numbers[0], lte: 0 <= numbers[0] }[cmp];
	}
	return negated ? !zero : zero;
}

/** Does a missing value match `node` where the refusal's place is its segment `at`? */
function missingMatchesAt(node, at, refusal) {
	const groupCount = at === 0 && isEntryCountPath(node)
		&& (refusal?.candidates || []).some(candidate => candidate && candidate.dataType === 'repeating-group');
	return groupCount ? countMatchesMissing(node) : NEGATIVE_CMPS.has(node.cmp);
}

/** Does the comparison at `path` (as written) stand at the refusal's place? */
const atPlace = (node, refusal) => {
	if (!node || node.op !== 'cmp' || node.facet || !Array.isArray(node.path)) return false;
	const key = refusal.choiceKey;
	if (refusal.reference || /^\{\{.*\}\}$/.test(key)) {
		const name = key.replace(/^\{\{|\}\}$/g, '');
		const refs = [];
		const visit = (value) => {
			if (!value || typeof value !== 'object') return;
			if (value.type === 'ref') refs.push(String(value.v));
			if (value.type === 'list') (value.v || []).forEach(visit);
		};
		visit(node.value);
		return refs.includes(name);
	}
	for (let i = 0; i < node.path.length; i += 1) {
		if (node.path.slice(0, i + 1).join('.') === key) return true;
	}
	return false;
};

/**
 * WHAT A PICK MAY BE IN TEXT THAT DECIDES WHO SEES WHAT (element ids step 3,
 * P3's security review and re-check): an access level's filter, a collection
 * link's, a public link's view, a dynamic group's rule. Where a missing
 * value matches at the refused place — `!=`, `not in`, `!~`, `is empty`, or
 * a comparison under `not` — one card's element would admit every OTHER
 * card's items (they hold no such value), so the server takes only "Any of
 * these cards"; a card the text pins changes nothing. The server says so
 * itself (`requiresAll`); this reads the text the same way, for a refusal
 * that does not. `{ groupOnly }`.
 */
export function accessPickScope(text, refusal) {
	const open = { groupOnly: false };
	if (!isPickableElementRefusal(refusal)) return open;
	let ast;
	try { ast = parse(String(text || '').trim()); } catch (err) { return open; }
	let groupOnly = false;
	const walk = (node, negated) => {
		if (!node || typeof node !== 'object') return;
		if (node.op === 'not') { walk(node.arg, !negated); return; }
		if (node.op === 'and' || node.op === 'or') { (node.args || []).forEach(arg => walk(arg, negated)); return; }
		if (!atPlace(node, refusal)) return;
		const at = node.path.findIndex((segment, i) => node.path.slice(0, i + 1).join('.') === refusal.choiceKey);
		const missingMatches = missingMatchesAt(node, at, refusal) !== negated;
		if (missingMatches) groupOnly = true;
	};
	walk(ast, false);
	return { groupOnly };
}

/** What the window says beside "Any of these cards" when that is all it may offer. */
export const ACCESS_GROUP_ONLY_HINT = 'This filter decides who sees what, and an item with no value meets this condition: picking one card’s element would also let in every other card’s items, so it reads every card’s.';

/** Must the pick be every candidate here — the server's `requiresAll`, or the text read as the server reads it? */
export const pickNeedsAll = (refusal, scope = null) => !!refusal?.requiresAll || !!scope?.groupOnly;

/**
 * The choices with this refusal's place answered — a NEW object; the
 * earlier picks stay (a text naming two ambiguous elements is refused for
 * each in turn).
 */
export function withElementChoice(choices, refusal, value) {
	const key = refusal?.choiceKey;
	const ids = (Array.isArray(value) ? value : [value]).map(str).map(id => id.replace(/^#/, '')).filter(id => ELEMENT_ID.test(id));
	if (!key || !ids.length) return { ...(choices || {}) };
	return { ...(choices || {}), [key]: ids.length === 1 && !Array.isArray(value) ? ids[0] : ids };
}

/** Does this map answer anything? */
export const hasElementChoices = choices => !!choices && typeof choices === 'object' && Object.keys(choices).length > 0;

/** The query parameter's value (JSON), or undefined when nothing was picked. */
export const elementChoicesParam = choices => (hasElementChoices(choices) ? JSON.stringify(choices) : undefined);

/** A body (or a params object) with the picks added — the same object when there are none. */
export function withElementChoicesField(payload, choices) {
	if (!hasElementChoices(choices)) return payload;
	return { ...(payload || {}), [ELEMENT_CHOICES_FIELD]: choices };
}

/** A query params object with the picks added as JSON — the same object when there are none. */
export function withElementChoicesParam(params, choices) {
	const value = elementChoicesParam(choices);
	return value === undefined ? params : { ...(params || {}), [ELEMENT_CHOICES_FIELD]: value };
}

/**
 * The server's sentence without the writer's label it starts with
 * (`query: …`, `condition: …`, `elements.visit.options.filter: …`): the
 * person never typed that word.
 */
export function refusalSentence(refusal) {
	const message = str(refusal?.message).trim();
	const field = str(refusal?.field).trim();
	if (field && message.startsWith(`${field}: `)) return message.slice(field.length + 2);
	return message.replace(/^[A-Za-z_][\w.[\]{}#-]*: /, '');
}

/** The title a refusal is shown under. */
export function elementRefusalTitle(refusal) {
	const element = str(refusal?.element);
	// An `#id` typed where every card's element is needed (who sees what): the
	// element by its label, never the id.
	const byId = element.startsWith('#')
		? (refusal?.candidates || []).find(candidate => candidate.elementId === element.slice(1)) || (refusal?.candidates || [])[0]
		: null;
	const named = element && !element.startsWith('#')
		? `“${element}”`
		: (byId ? `“${byId.label || byId.name}”` : 'An element this filter names');
	if (isElementConfirmation(refusal)) return `${named} is one element under more than one name`;
	if (refusal?.code === 'ambiguous_element') return `${named} could be more than one element`;
	return element && !element.startsWith('#')
		? `${named} isn’t an element here`
		: 'This filter names an element that isn’t here';
}

/** The title a too-long text is shown under. */
export const TOO_LONG_TITLE = 'This filter is too long';

/**
 * One line that says what a refusal lists, for a surface that can only show
 * text: "“status” could be more than one element: Status on Task, State on
 * Bug." — or the server's own sentence for an unknown element.
 */
export function elementRefusalText(refusal) {
	if (!refusal) return '';
	if (isUnpickableElementRefusal(refusal)) return refusalSentence(refusal) || `${elementRefusalTitle(refusal)}.`;
	if (isElementConfirmation(refusal)) return confirmationSentence(refusal);
	if (refusal.code === 'ambiguous_element' && refusal.candidates.length) {
		return `${elementRefusalTitle(refusal)}: ${refusal.candidates.map(candidateLabel).join(', ')}.`;
	}
	return refusalSentence(refusal) || `${elementRefusalTitle(refusal)}.`;
}

/**
 * For a surface with no filter of its own to edit (a link made from the list
 * on screen): an element refusal's one line — what the name could be, or
 * that it names nothing — or null for any other failure.
 */
export function elementRefusalMessage(error) {
	const refusal = error?.response ? elementRefusalOfError(error) : elementRefusalOf(error);
	return refusal ? elementRefusalText(refusal) : null;
}

/**
 * The card element whose OWN text a refusal is about — `elements.visit.
 * options.filter` (a lookup's picker filter), `elements.total.options.
 * drivenFrom.where.filter` (a driven total's) → `visit`, `total` — or null
 * (a card's worklist filter is `filter`). A refusal's `element` is the name
 * the TEXT holds, never the row it sits on: the card editor opens this one.
 */
export function ownerElementOfField(field) {
	const match = /^elements\.([^.]+)\.options\./.exec(String(field || ''));
	return match ? match[1] : null;
}

const ID_SEGMENT = id => `#${String(id).replace(/^#/, '')}`;

/**
 * The text with a refusal's place answered IN the text — the pick written as
 * the element's id, or, for "Any of these cards", as the element group (the
 * comparison once per element, joined with `or`, or `and` where a missing
 * value matches) — exactly as the server writes it for `elementChoices`. For
 * a filter the server reads per chart and never stores (a dashboard run's,
 * whose charts' cards each list their own candidates): one text answers
 * every chart. The text as given when it does not parse or holds nothing at
 * that place.
 */
export function writeElementPick(text, refusal, value) {
	const trimmed = String(text || '').trim();
	const ids = (Array.isArray(value) ? value : [value]).map(id => String(id || '').replace(/^#/, '')).filter(id => /^[a-z0-9]{8}$/.test(id));
	if (!trimmed || !refusal || !ids.length) return text;
	let ast;
	try { ast = parse(trimmed); } catch (err) { return text; }
	const key = String(refusal.choiceKey || '');
	const reference = refusal.reference || /^\{\{.*\}\}$/.test(key);
	const refName = key.replace(/^\{\{|\}\}$/g, '');
	let changed = false;
	const join = (node, branches, at = -1) => (branches.length === 1 ? branches[0] : { op: missingMatchesAt(node, at, refusal) ? 'and' : 'or', args: branches });
	const withRef = (value, id) => {
		if (!value || typeof value !== 'object') return value;
		if (value.type === 'list') return { ...value, v: (value.v || []).map(member => withRef(member, id)) };
		return value.type === 'ref' && String(value.v) === refName ? { ...value, v: ID_SEGMENT(id) } : value;
	};
	const walk = (node) => {
		if (!node || typeof node !== 'object') return node;
		if (node.op === 'and' || node.op === 'or') return { ...node, args: (node.args || []).map(walk) };
		if (node.op === 'not') return { ...node, arg: walk(node.arg) };
		if (node.op !== 'cmp' || node.facet || !Array.isArray(node.path)) return node;
		if (reference) {
			if (!atPlace(node, refusal)) return node;
			changed = true;
			return join(node, ids.map(id => ({ ...node, value: withRef(node.value, id) })));
		}
		const at = node.path.findIndex((segment, i) => node.path.slice(0, i + 1).join('.') === key);
		if (at === -1) return node;
		changed = true;
		return join(node, ids.map((id) => {
			const next = { ...node, path: node.path.map((segment, i) => (i === at ? ID_SEGMENT(id) : segment)) };
			if (at === 0) delete next.forceElement;
			return next;
		}), at);
	};
	const next = walk(ast);
	return changed ? serialize(next) : text;
}

/**
 * A card's save with each pick written into the filter it answers (the P4
 * review's R-CD2): one save carries ONE `elementChoices` map, keyed by the
 * name alone, so two of a card's filters naming the same ambiguous name —
 * a lookup's and the worklist's, each with its own candidates — could never
 * both be answered by it. `fieldPicks` is `{ <refusal field>: { <choiceKey>:
 * id | [ids] } }`; a field is `elements.<name>.options.<path>` (a lookup's
 * picker filter, a driven total's filter) or `filter` (the worklist's). The
 * text is written as the server writes a pick (`writeElementPick`). A NEW
 * payload — the draft is never touched; a field it cannot find is left to
 * the map.
 */
export function withFieldPicks(payload, fieldPicks) {
	const entries = Object.entries(fieldPicks || {});
	if (!payload || !entries.length) return payload;
	const writeAll = (text, picks) => Object.entries(picks || {}).reduce(
		(acc, [choiceKey, value]) => writeElementPick(acc, { choiceKey, reference: /^\{\{.*\}\}$/.test(choiceKey) }, value),
		text
	);
	const setPath = (obj, keys, fn) => {
		if (!obj || typeof obj !== 'object') return obj;
		const [head, ...rest] = keys;
		if (!(head in obj)) return obj;
		if (!rest.length) return typeof obj[head] === 'string' ? { ...obj, [head]: fn(obj[head]) } : obj;
		return { ...obj, [head]: setPath(obj[head], rest, fn) };
	};
	let next = payload;
	entries.forEach(([field, picks]) => {
		if (field === 'filter') {
			next = setPath(next, ['options', 'worklist', 'filter'], text => writeAll(text, picks));
			return;
		}
		const match = /^elements\.([^.]+)\.(options\..+)$/.exec(field);
		if (!match || !Array.isArray(next.elements)) return;
		const [, name, rest] = match;
		next = {
			...next,
			elements: next.elements.map(el => (el && el.name === name ? setPath(el, rest.split('.'), text => writeAll(text, picks)) : el)),
		};
	});
	return next;
}

/** Where a link made from the list on screen is answered: the list's own filter. */
export const LINK_PICK_HINT = 'Pick the element in + Filter, then make the link again.';
